import { Stack } from '@mui/joy';
import { QueryClientProvider } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';

import Loading from '@MP/shared/components/Loading';
import { useMobileQueryClient } from '@MP/shared/hooks/useMobileQueryClient';
import { getApiErrorPayload } from '@MP/shared/utils/ApiErrorUtils';

import { DeletedSession } from './mobile/DeletedSession';
import { ExpiredToken } from './mobile/ExpiredToken';
import { InvalidAccess } from './mobile/InvalidAccess';

/**
 * 모바일 레이아웃 컴포넌트
 * 모바일 환경에서 사용되는 레이아웃으로, 토큰 기반 인증 및 에러 처리를 담당합니다.
 */
const MobileLayout = () => {
  const [loading, setLoading] = useState(true);
  const [expiredToken, setExpiredToken] = useState<boolean>(false);
  const [deletedSession, setDeletedSession] = useState<boolean>(false);
  // URL에 token 파라미터가 없으면 최초부터 무효 접근으로 판정한다(마운트 시점의 URL은 불변).
  const [invalidToken, setInvalidToken] = useState<boolean>(
    () => !new URL(document.location.href).searchParams.get('token')
  );

  const valid = !deletedSession && !expiredToken && !invalidToken;

  // URL 파라미터에서 토큰을 추출하여 Authorization 헤더에 설정
  useEffect(() => {
    const tokenStr = new URL(document.location.href).searchParams.get('token');
    if (tokenStr) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + tokenStr;
    }
  }, []);

  // 초기 로딩 상태 관리 (1초 후 로딩 완료)
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  /**
   * API 에러 처리 함수
   * 에러 코드에 따라 적절한 에러 상태를 설정합니다.
   * - EXPIRED_JWT, NOT_FOUND_SUBJECT_META, NOT_FOUND_SURVEY_SESSION, ALREADY_ANSWERED_SESSION_WHEN_UNREGISTERED_USER: 토큰 만료
   * - DELETED_SURVEY_SESSION: 세션 삭제됨
   * - UNAUTHORIZED: 인증 실패
   */
  const errFn = useCallback((error: AxiosError) => {
    const res = getApiErrorPayload(error);
    if (
      res?.code === 'EXPIRED_JWT' ||
      res?.code === 'NOT_FOUND_SUBJECT_META' ||
      res?.code === 'NOT_FOUND_SURVEY_SESSION' ||
      res?.code === 'ALREADY_ANSWERED_SESSION_WHEN_UNREGISTERED_USER'
    ) {
      setExpiredToken(true);
    } else if (res?.code === 'DELETED_SURVEY_SESSION') {
      setDeletedSession(true);
    } else if (res?.code === 'UNAUTHORIZED') {
      setInvalidToken(true);
    }
  }, []);

  const queryClient = useMobileQueryClient(errFn);

  if (loading) {
    return <Loading />;
  }

  return (
    <Stack height={'100vh'} bgcolor={'#FFF'} overflow={'auto'}>
      <QueryClientProvider client={queryClient}>{valid && <Outlet />}</QueryClientProvider>

      {expiredToken && <ExpiredToken />}
      {deletedSession && <DeletedSession />}
      {invalidToken && <InvalidAccess />}
    </Stack>
  );
};

export { MobileLayout };
